import * as THREE from 'three';
import { makeFloorMaterial, makePisteMetal, makePisteWood } from './materials';

const SPONSOR_BANNER_URL = '/brand/leon-paul-sponsor-banner.png';

/** Competition strip + venue — dark hall with dramatic lighting. */
export function createVenue(): THREE.Group {
  const root = new THREE.Group();

  // Arena floor
  const floor = new THREE.Mesh(
    new THREE.PlaneGeometry(40, 24),
    makeFloorMaterial(),
  );
  floor.rotation.x = -Math.PI / 2;
  floor.receiveShadow = true;
  root.add(floor);

  // Raised podium
  const podium = new THREE.Mesh(
    new THREE.BoxGeometry(18, 0.12, 3.2),
    makePisteWood(),
  );
  podium.position.y = 0.06;
  podium.receiveShadow = true;
  podium.castShadow = true;
  root.add(podium);

  // Metallic piste (14m FIE length ≈ visual 14 units)
  const metal = makePisteMetal();
  const strip = new THREE.Mesh(new THREE.BoxGeometry(14.2, 0.04, 1.6), metal);
  strip.position.y = 0.14;
  strip.receiveShadow = true;
  root.add(strip);

  // Center line
  const center = new THREE.Mesh(
    new THREE.BoxGeometry(0.04, 0.045, 1.55),
    new THREE.MeshStandardMaterial({ color: 0x222, roughness: 0.6 }),
  );
  center.position.y = 0.155;
  root.add(center);

  // On-guard lines (±2m)
  for (const x of [-2, 2]) {
    const line = new THREE.Mesh(
      new THREE.BoxGeometry(0.03, 0.045, 1.55),
      new THREE.MeshStandardMaterial({ color: 0x111, roughness: 0.6 }),
    );
    line.position.set(x, 0.155, 0);
    root.add(line);
  }

  // Warning / end lines
  for (const x of [-7, 7]) {
    const line = new THREE.Mesh(
      new THREE.BoxGeometry(0.06, 0.05, 1.6),
      new THREE.MeshStandardMaterial({ color: 0xcc3333, emissive: 0x440000, roughness: 0.5 }),
    );
    line.position.set(x, 0.155, 0);
    root.add(line);
  }

  // Side markings
  for (const z of [-0.82, 0.82]) {
    const edge = new THREE.Mesh(
      new THREE.BoxGeometry(14.2, 0.05, 0.04),
      new THREE.MeshStandardMaterial({ color: 0x333844, metalness: 0.7, roughness: 0.4 }),
    );
    edge.position.set(0, 0.155, z);
    root.add(edge);
  }

  // Scoring machine pedestal
  const machine = createScoringMachine();
  machine.position.set(0, 0.12, -2.4);
  root.add(machine);

  // Backdrop walls
  const wallMat = new THREE.MeshStandardMaterial({
    color: 0x0a0e16,
    metalness: 0.3,
    roughness: 0.85,
  });
  const back = new THREE.Mesh(new THREE.PlaneGeometry(40, 12), wallMat);
  back.position.set(0, 6, -8);
  root.add(back);

  // Leon Paul sponsor branding behind the piste
  root.add(createSponsorBanner());

  const left = new THREE.Mesh(new THREE.PlaneGeometry(20, 12), wallMat);
  left.position.set(-14, 6, 0);
  left.rotation.y = Math.PI / 2;
  root.add(left);

  const right = left.clone();
  right.position.x = 14;
  right.rotation.y = -Math.PI / 2;
  root.add(right);

  // Ceiling truss lights
  const trussMat = new THREE.MeshStandardMaterial({ color: 0x1a1e28, metalness: 0.8, roughness: 0.4 });
  for (const x of [-5, 0, 5]) {
    const truss = new THREE.Mesh(new THREE.BoxGeometry(0.15, 0.15, 8), trussMat);
    truss.position.set(x, 7.5, 0);
    root.add(truss);
    const lightBar = new THREE.Mesh(
      new THREE.BoxGeometry(2.5, 0.08, 0.3),
      new THREE.MeshStandardMaterial({
        color: 0xffffff,
        emissive: 0xaaccff,
        emissiveIntensity: 0.8,
        metalness: 0.5,
        roughness: 0.3,
      }),
    );
    lightBar.position.set(x, 7.2, 0);
    root.add(lightBar);
  }

  // Ambient particles / dust (simple points)
  const dustCount = 200;
  const positions = new Float32Array(dustCount * 3);
  for (let i = 0; i < dustCount; i++) {
    positions[i * 3] = (Math.random() - 0.5) * 20;
    positions[i * 3 + 1] = Math.random() * 6 + 0.5;
    positions[i * 3 + 2] = (Math.random() - 0.5) * 10;
  }
  const dustGeo = new THREE.BufferGeometry();
  dustGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  const dust = new THREE.Points(
    dustGeo,
    new THREE.PointsMaterial({
      color: 0x88aacc,
      size: 0.03,
      transparent: true,
      opacity: 0.35,
      depthWrite: false,
    }),
  );
  dust.name = 'dust';
  root.add(dust);

  // Soft ground fog plane
  const fogPlane = new THREE.Mesh(
    new THREE.PlaneGeometry(30, 12),
    new THREE.MeshBasicMaterial({
      color: 0x0a1220,
      transparent: true,
      opacity: 0.35,
      depthWrite: false,
    }),
  );
  fogPlane.rotation.x = -Math.PI / 2;
  fogPlane.position.y = 0.02;
  root.add(fogPlane);

  // Spectator silhouettes (far, simple cards for depth)
  const silMat = new THREE.MeshBasicMaterial({
    color: 0x05070c,
    transparent: true,
    opacity: 0.55,
  });
  for (let i = 0; i < 12; i++) {
    const h = 1.4 + Math.random() * 0.5;
    const sil = new THREE.Mesh(new THREE.CapsuleGeometry(0.18, h * 0.55, 3, 6), silMat);
    sil.position.set((i - 5.5) * 1.1 + (Math.random() - 0.5) * 0.3, h * 0.55, -3.6 - Math.random() * 0.8);
    root.add(sil);
  }

  // Side banners
  for (const x of [-9, 9]) {
    const banner = new THREE.Mesh(
      new THREE.PlaneGeometry(2.4, 1.2),
      new THREE.MeshStandardMaterial({
        color: 0x101820,
        emissive: x < 0 ? 0x0a2040 : 0x401018,
        emissiveIntensity: 0.4,
        metalness: 0.3,
        roughness: 0.6,
      }),
    );
    banner.position.set(x, 2.2, -6.5);
    root.add(banner);
  }

  return root;
}

/** Large “Sponsored by · Leon Paul” board on the back wall. */
function createSponsorBanner(): THREE.Group {
  const g = new THREE.Group();
  g.name = 'sponsorBanner';

  // Frame / lightbox
  const frame = new THREE.Mesh(
    new THREE.BoxGeometry(7.2, 2.0, 0.12),
    new THREE.MeshStandardMaterial({
      color: 0x12161f,
      metalness: 0.65,
      roughness: 0.35,
    }),
  );
  frame.position.set(0, 3.4, -7.85);
  frame.castShadow = true;
  g.add(frame);

  const bezel = new THREE.Mesh(
    new THREE.BoxGeometry(7.4, 2.2, 0.06),
    new THREE.MeshStandardMaterial({
      color: 0x2a3344,
      metalness: 0.8,
      roughness: 0.3,
      emissive: 0x0a1520,
      emissiveIntensity: 0.25,
    }),
  );
  bezel.position.set(0, 3.4, -7.9);
  g.add(bezel);

  const tex = new THREE.TextureLoader().load(SPONSOR_BANNER_URL);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;

  const board = new THREE.Mesh(
    new THREE.PlaneGeometry(6.8, 1.75),
    new THREE.MeshStandardMaterial({
      map: tex,
      color: 0xffffff,
      metalness: 0.15,
      roughness: 0.55,
      emissive: 0xffffff,
      emissiveMap: tex,
      emissiveIntensity: 0.22,
      transparent: true,
    }),
  );
  board.position.set(0, 3.4, -7.78);
  g.add(board);

  // Soft wash light on the banner
  const spot = new THREE.SpotLight(0xaaccff, 18, 18, 0.55, 0.45, 1.2);
  spot.position.set(0, 6.5, -4);
  spot.target.position.set(0, 3.4, -7.8);
  g.add(spot, spot.target);

  // Smaller side boards
  for (const x of [-8.5, 8.5]) {
    const side = board.clone();
    side.scale.set(0.45, 0.45, 1);
    side.position.set(x, 2.8, -7.78);
    g.add(side);
  }

  return g;
}

function createScoringMachine(): THREE.Group {
  const g = new THREE.Group();
  const body = new THREE.Mesh(
    new THREE.BoxGeometry(1.4, 0.9, 0.35),
    new THREE.MeshStandardMaterial({ color: 0x12151c, metalness: 0.6, roughness: 0.4 }),
  );
  body.position.y = 0.55;
  body.castShadow = true;
  g.add(body);

  // Lights panels (left green / right red — classic scoring box)
  const left = new THREE.Mesh(
    new THREE.BoxGeometry(0.35, 0.35, 0.05),
    new THREE.MeshStandardMaterial({
      color: 0x0a2a18,
      emissive: 0x00ff66,
      emissiveIntensity: 0.15,
    }),
  );
  left.position.set(-0.35, 0.65, 0.18);
  left.name = 'scoreLightL';
  g.add(left);

  const right = new THREE.Mesh(
    new THREE.BoxGeometry(0.35, 0.35, 0.05),
    new THREE.MeshStandardMaterial({
      color: 0x2a0a10,
      emissive: 0xff3355,
      emissiveIntensity: 0.15,
    }),
  );
  right.position.set(0.35, 0.65, 0.18);
  right.name = 'scoreLightR';
  g.add(right);

  const white = new THREE.Mesh(
    new THREE.BoxGeometry(0.2, 0.12, 0.05),
    new THREE.MeshStandardMaterial({
      color: 0x222,
      emissive: 0xffffff,
      emissiveIntensity: 0.05,
    }),
  );
  white.position.set(0, 0.45, 0.18);
  white.name = 'scoreLightW';
  g.add(white);

  // Brand plate
  const plate = new THREE.Mesh(
    new THREE.BoxGeometry(0.5, 0.12, 0.02),
    new THREE.MeshStandardMaterial({ color: 0x2a3344, metalness: 0.7, roughness: 0.3 }),
  );
  plate.position.set(0, 0.3, 0.18);
  g.add(plate);

  return g;
}

export function pulseScoreLight(
  venue: THREE.Group,
  which: 'L' | 'R' | 'W',
  on: boolean,
): void {
  const name = which === 'L' ? 'scoreLightL' : which === 'R' ? 'scoreLightR' : 'scoreLightW';
  const mesh = venue.getObjectByName(name) as THREE.Mesh | undefined;
  if (!mesh) return;
  const mat = mesh.material as THREE.MeshStandardMaterial;
  if (which === 'W') {
    mat.emissiveIntensity = on ? 1.2 : 0.05;
  } else {
    mat.emissiveIntensity = on ? 2.2 : 0.15;
  }
}
